import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import { tableInCardSx } from "@/components/common/unifiedTableSx";

export interface ReportSummaryColumn<Row> {
  id: string;
  label: React.ReactNode;
  mobileLabel?: string;
  mobileRole?: "identity" | "summary" | "detail";
  align?: "center" | "left" | "right";
  sortValue: (row: Row) => string | number;
  render: (row: Row) => React.ReactNode;
}

interface ReportSummaryTableProps<Row> {
  columns: ReadonlyArray<ReportSummaryColumn<Row>>;
  rows: ReadonlyArray<Row>;
  getRowId: (row: Row) => string | number;
  initialOrderBy: string;
  initialOrder?: "asc" | "desc";
  loading?: boolean;
  loadingRows?: number;
  emptyState: React.ReactNode;
  sort?: { orderBy: string; order: "asc" | "desc"; onSort: (column: string) => void };
  onRowActivate?: (row: Row) => void;
  isRowSelected?: (row: Row) => boolean;
  resetKey?: string;
}

export const ReportSummaryTable = <Row,>({
  columns,
  rows,
  getRowId,
  initialOrderBy,
  initialOrder = "desc",
  loading = false,
  loadingRows = 5,
  emptyState,
  sort,
  onRowActivate,
  isRowSelected,
  resetKey,
}: ReportSummaryTableProps<Row>) => {
  const [internalOrderBy, setOrderBy] = useState(initialOrderBy);
  const [internalOrder, setOrder] = useState<"asc" | "desc">(initialOrder);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const orderBy = sort?.orderBy ?? internalOrderBy;
  const order = sort?.order ?? internalOrder;
  const handleSort = (id: string) => {
    if (sort) {
      sort.onSort(id);
      return;
    }
    setOrder(orderBy === id && order === "asc" ? "desc" : "asc");
    setOrderBy(id);
  };
  const sortedRows = useMemo(() => {
    if (sort) return [...rows];
    const column = columns.find((candidate) => candidate.id === orderBy) ?? columns[0];
    if (!column) return [...rows];
    return [...rows].sort((a, b) => {
      const aValue = column.sortValue(a);
      const bValue = column.sortValue(b);
      const comparison =
        typeof aValue === "number" && typeof bValue === "number"
          ? aValue - bValue
          : String(aValue).localeCompare(String(bValue));
      return order === "asc" ? comparison : -comparison;
    });
  }, [columns, order, orderBy, rows, sort]);

  if (isMobile) {
    const identity = columns.find((column) => column.mobileRole === "identity") ?? columns[0];
    const summary = columns
      .filter((column) => column !== identity && column.mobileRole !== "detail")
      .slice(0, 2);
    const details = columns.filter((column) => column !== identity && !summary.includes(column));
    return (
      <Box sx={{ minWidth: 0 }}>
        <RecordSortControl
          value={orderBy}
          direction={order}
          options={columns.map((column) => ({
            value: column.id,
            label:
              typeof column.label === "string"
                ? column.label
                : (column.mobileLabel ?? t("common.breadcrumbs.details")),
          }))}
          onSort={handleSort}
        />
        <RecordList
          items={sortedRows}
          getItemId={getRowId}
          loading={loading}
          emptyState={emptyState}
          resetKey={`${resetKey ?? ""}:${orderBy}:${order}`}
          renderTitle={(row) =>
            onRowActivate ? (
              <Button
                type="button"
                onClick={() => onRowActivate(row)}
                aria-pressed={isRowSelected?.(row)}
                sx={{ p: 0, justifyContent: "flex-start", textAlign: "start" }}
              >
                {identity?.render(row)}
              </Button>
            ) : (
              identity?.render(row)
            )
          }
          renderSummary={(row) => (
            <RecordFields
              fields={summary.map((column) => ({ label: column.label, value: column.render(row) }))}
            />
          )}
          renderDetails={
            details.length
              ? (row) => (
                  <RecordFields
                    fields={details.map((column) => ({
                      label: column.label,
                      value: column.render(row),
                    }))}
                  />
                )
              : undefined
          }
        />
      </Box>
    );
  }

  return (
    <Table sx={tableInCardSx}>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              align={column.align ?? "left"}
              sortDirection={orderBy === column.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : "asc"}
                onClick={() => handleSort(column.id)}
              >
                {column.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableSkeletonRows
            alignments={columns.map((column) => column.align)}
            columnCount={columns.length}
            rowCount={loadingRows}
          />
        ) : sortedRows.length > 0 ? (
          sortedRows.map((row) => (
            <TableRow
              key={getRowId(row)}
              role={onRowActivate ? "button" : undefined}
              tabIndex={onRowActivate ? 0 : undefined}
              aria-pressed={isRowSelected?.(row)}
              onClick={onRowActivate ? () => onRowActivate(row) : undefined}
              onKeyDown={
                onRowActivate
                  ? (event) => {
                      if (
                        event.target === event.currentTarget &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        onRowActivate(row);
                      }
                    }
                  : undefined
              }
              sx={
                onRowActivate
                  ? {
                      cursor: "pointer",
                      "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "primary.main",
                        outlineOffset: -2,
                      },
                    }
                  : undefined
              }
            >
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align ?? "left"}>
                  {column.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="table-empty-state">
            <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
              {emptyState}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
